import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Recommendation {
  id: string
  type: string
  item_name: string
  score: number
  reason: string
  confidence: number
  expected_conversion: number
  priority: number
}

interface Algorithm {
  id: string
  name: string
  status: string
  accuracy: number
  coverage: number
  diversity: number
  latency_ms: number
}

interface Statistics {
  total_recommendations: number
  unique_items: number
  active_algorithms: number
  avg_recommendation_score: number
  click_through_rate: number
  conversion_rate: number
  revenue_lift: number
  personalization_coverage: number
}

export default function RecommendationEnginePage() {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [algorithms, setAlgorithms] = useState<Algorithm[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'recommendations' | 'algorithms'>('recommendations')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [recRes, algoRes, statsRes] = await Promise.all([
        fetch('/api/recommendationengine/recommendations'),
        fetch('/api/recommendationengine/algorithms'),
        fetch('/api/recommendationengine/statistics')
      ])

      if (recRes.ok) setRecommendations(await recRes.json())
      if (algoRes.ok) setAlgorithms(await algoRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching recommendation engine data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      testing: 'bg-yellow-100 text-yellow-800',
      deprecated: 'bg-gray-100 text-gray-800'
    }
    return classes[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Recommendation Engine</h1>
          <p className="mt-2 text-gray-600">AI-powered product and content recommendations</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Recommendations</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_recommendations.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Click-Through Rate</p>
              <p className="text-2xl font-bold text-blue-600">{(statistics.click_through_rate * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Conversion Rate</p>
              <p className="text-2xl font-bold text-green-600">{(statistics.conversion_rate * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Revenue Lift</p>
              <p className="text-2xl font-bold text-purple-600">{(statistics.revenue_lift * 100).toFixed(1)}%</p>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('recommendations')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'recommendations' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Recommendations ({recommendations.length})
            </button>
            <button
              onClick={() => setActiveTab('algorithms')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'algorithms' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Algorithms ({algorithms.length})
            </button>
          </nav>
        </div>

        {activeTab === 'recommendations' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendations.slice(0, 12).map((rec) => (
              <div key={rec.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <span className="text-xs text-blue-600 font-medium uppercase">{rec.type}</span>
                  <span className="text-xs text-gray-500">Priority: {rec.priority}</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mt-2">{rec.item_name}</h3>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Score</span>
                    <span className="font-medium">{(rec.score * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Confidence</span>
                    <span className="font-medium">{(rec.confidence * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Expected Conv.</span>
                    <span className="font-medium text-green-600">{(rec.expected_conversion * 100).toFixed(1)}%</span>
                  </div>
                </div>
                <p className="mt-3 text-xs text-gray-500 capitalize">{rec.reason.replace('_', ' ')}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'algorithms' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Algorithm</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accuracy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coverage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Diversity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Latency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {algorithms.map((algo) => (
                  <tr key={algo.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{algo.name}</td>
                    <td className="px-6 py-4 text-gray-500">{(algo.accuracy * 100).toFixed(1)}%</td>
                    <td className="px-6 py-4 text-gray-500">{(algo.coverage * 100).toFixed(1)}%</td>
                    <td className="px-6 py-4 text-gray-500">{(algo.diversity * 100).toFixed(1)}%</td>
                    <td className="px-6 py-4 text-gray-500">{algo.latency_ms.toFixed(0)}ms</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(algo.status)}`}>
                        {algo.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
